import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  PageContainer,
  PageHeader,
  PageTitle,
  PageIcon,
  PageDescription,
  ContentGrid,
  ContentRow,
  Card,
  CardTitle,
  CardContent,
  ActionBar,
  Button,
  SearchInput,
  Table,
  TableHeader,
  TableRow,
  TableCell,
  StatusBadge,
  TabContainer,
  TabList,
  Tab,
  TabContent,
  fadeIn,
  formatDate
} from '../styles/PageStyles';
import { faKeyboard } from '@fortawesome/free-solid-svg-icons';
import PageHeaderWithInfo from '../components/common/PageHeaderWithInfo';
import { getPromptCount, getPrompts } from '../services/promptService';
import { useNavigate } from 'react-router-dom';
import Pagination from '../components/common/Pagination';

const PromptLibrary = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('prompts');
  const [selectedPrompt, setSelectedPrompt] = useState(null);
  const [promptList, setPromptList] = useState([null]);
  const [promptTemplateList, setPromptTemplateList] = useState([null]);

  const [totalPages, setTotalPages] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  
  const itemsPerPage = 10;

  const navigate = useNavigate();

 
  useEffect(() => {
    const fetchPrompts = async () => {
      const query = `skip=${(currentPage - 1) * itemsPerPage}&limit=${itemsPerPage}`;
      try {
        const response = await getPrompts({}, {}, query);
        setPromptList(response);
      } catch (error) {
        console.error('Error fetching users:', error);
      }
    };
    fetchPrompts();

  }, [currentPage]);

  useEffect(() => {
    const fetchPromptCount = async () => {
      try {
        const count = await getPromptCount();
        setTotalItems(count);
      } catch (error) {
        console.log("Error while fetching prompt count", error)
      }
    }

    fetchPromptCount();
  }, [])

  // Filter prompts based on search term
  const filteredPrompts = promptList.filter(prompt =>
    prompt?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    prompt?.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    prompt?.prompt?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Get status badge color
  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'active';
      case 'draft': return 'default';
      case 'deprecated': return 'warning';
      default: return 'default';
    }
  };

  // Get usage badge color
  const getUsageColor = (usage) => {
    switch (usage) {
      case 'Very High': return 'error';
      case 'High': return 'warning';
      case 'Medium': return 'active';
      case 'Low': return 'default';
      default: return 'default';
    }
  };

  // Handle prompt selection
  const handlePromptSelect = (prompt) => {
    // setSelectedPrompt(prompt);
    navigate(`/prompt-details/${prompt}`)
  };

  return (
    <PageContainer>
      <motion.div
        initial="hidden"
        animate="visible"
        variants={fadeIn}
      >
        <PageHeaderWithInfo
          icon={faKeyboard}
          title="Prompt Library"
          description="Manage, version, and optimize prompts used by AI agents across the system.
          Create templates, track performance, and ensure consistency in AI interactions."
        >
          <div style={{ display: 'flex', gap: '1rem' }}>
            <SearchInput
              type="text"
              placeholder="Search prompts..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <Button>Import Prompts</Button>
            <Button primary onClick={() => handlePromptSelect('new')}>Create Prompt</Button>
          </div>
        </PageHeaderWithInfo>

        <TabContainer>
          <TabList>
            <Tab active={activeTab === 'prompts'} onClick={() => setActiveTab('prompts')}>Prompts</Tab>
            <Tab active={activeTab === 'templates'} onClick={() => setActiveTab('templates')}>Templates</Tab>
            <Tab active={activeTab === 'analytics'} onClick={() => setActiveTab('analytics')}>Analytics</Tab>
          </TabList>

          {/* <ActionBar>
            <SearchInput
              type="text"
              placeholder="Search prompts..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <Button>Filter</Button>
            <Button>Sort</Button>
          </ActionBar> */}

          <TabContent active={activeTab === 'prompts'}>
            <ContentRow>
              <div style={{ flex: '1 1 60%' }}>
                <>
                  <Card>
                  <CardTitle>Prompt Library</CardTitle>
                  <Table>
                    <thead>
                      <tr>
                        <TableHeader>Name</TableHeader>
                        <TableHeader>Category</TableHeader>
                        <TableHeader>Version</TableHeader>
                        <TableHeader>Last Modified</TableHeader>
                        <TableHeader>Author</TableHeader>
                        <TableHeader>Usage</TableHeader>
                        <TableHeader>Performance</TableHeader>
                        <TableHeader>Status</TableHeader>
                        <TableHeader>Actions</TableHeader>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredPrompts.map(prompt => (
                        <TableRow
                          key={prompt?.id}
                          onClick={() => handlePromptSelect(prompt.code)}
                          style={{ cursor: 'pointer', background: selectedPrompt?.id === prompt?.id ? 'rgba(0, 102, 255, 0.05)' : 'transparent' }}
                        >
                          <TableCell>{prompt?.name}</TableCell>
                          <TableCell>{prompt?.category}</TableCell>
                          <TableCell>{prompt?.version}</TableCell>
                          <TableCell>{formatDate(prompt?.updated_at)}</TableCell>
                          <TableCell>{prompt?.created_by}</TableCell>
                          <TableCell>
                            <StatusBadge status={getUsageColor(prompt?.usage)}>
                              {prompt?.usage}
                            </StatusBadge>
                          </TableCell>
                          <TableCell>{prompt?.performance}%</TableCell>
                          <TableCell>
                            <StatusBadge status={getStatusColor(prompt?.status)}>
                              {prompt?.status?.charAt(0).toUpperCase() + prompt?.status?.slice(1)}
                            </StatusBadge>
                          </TableCell>
                          <TableCell>
                            <div style={{ display: 'flex', gap: '0.5rem' }}>
                              <Button style={{ padding: '0.3rem 0.6rem', fontSize: '0.8rem' }}>Edit</Button>
                              {/* <Button primary style={{ padding: '0.3rem 0.6rem', fontSize: '0.8rem' }}>Version</Button> */}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </tbody>
                  </Table>
                </Card>
            
                <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    totalCount={totalItems}
                    setCurrentPage={setCurrentPage}
                    setTotalPages={setTotalPages}
                   />
                </>
              </div>

            </ContentRow>
          </TabContent>

          <TabContent active={activeTab === 'templates'}>
            <ContentGrid>
              {['Claims', 'Underwriting', 'Customer Service', 'Fraud', 'Document Processing'].map((category, index) => (
                <Card key={index}>
                  <CardTitle>{category} Templates</CardTitle>
                  <CardContent>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', marginTop: '0.5rem' }}>
                      {promptList
                        .filter(prompt => prompt?.category === category && prompt?.is_template === true)
                        .map((prompt, idx) => (
                          <div
                            key={idx}
                            style={{
                              padding: '1rem',
                              background: 'rgba(0, 102, 255, 0.05)',
                              borderRadius: '8px',
                              cursor: 'pointer'
                            }}
                            onClick={() => {
                              setActiveTab('prompts');
                              handlePromptSelect(prompt);
                            }}
                          >
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                              <div style={{ fontWeight: 'bold' }}>{prompt?.name}</div>
                              <StatusBadge status={getStatusColor(prompt?.status)}>
                                {prompt?.status.charAt(0).toUpperCase() + prompt?.status.slice(1)}
                              </StatusBadge>
                            </div>
                            <div style={{ fontSize: '0.9rem', color: 'var(--text-secondary)', marginBottom: '0.5rem' }}>
                              {prompt?.description}
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem' }}>
                              <div>v{prompt?.version}</div>
                              <div>{prompt?.performance}% performance</div>
                            </div>
                          </div>
                        ))
                      }

                      {/* <Button style={{ alignSelf: 'center', marginTop: '0.5rem' }}>
                        Add {category} Template
                      </Button> */}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </ContentGrid>
          </TabContent>

          <TabContent active={activeTab === 'analytics'}>
            <ContentGrid>
              <Card>
                <CardTitle>Prompt Performance</CardTitle>
                <CardContent>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', marginTop: '1rem' }}>
                    {promptList.map((prompt, index) => (
                      <div key={index}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.3rem' }}>
                          <span>{prompt?.name}</span>
                          <span>{prompt?.performance}%</span>
                        </div>
                        <div style={{ width: '100%', height: '8px', background: 'rgba(0,102,255,0.1)', borderRadius: '4px' }}>
                          <div
                            style={{
                              width: `${prompt?.performance}%`,
                              height: '8px',
                              background: prompt?.performance >= 95 ? '#34C759' : prompt?.performance >= 90 ? 'var(--primary)' : '#FFCC00',
                              borderRadius: '4px'
                            }}
                          ></div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardTitle>Prompt Usage</CardTitle>
                <CardContent>
                  <div style={{ height: '200px', display: 'flex', alignItems: 'flex-end', gap: '12px', marginTop: '20px' }}>
                    {[1250, 980, 1420, 1680, 1520, 1850, 2100].map((value, index) => (
                      <div key={index} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <div style={{ height: `${value / 15}px`, width: '100%', background: 'var(--primary)', borderRadius: '4px 4px 0 0' }}></div>
                        <div style={{ fontSize: '0.7rem', marginTop: '5px' }}>{`D${index + 1}`}</div>
                      </div>
                    ))}
                  </div>
                  <div style={{ textAlign: 'center', marginTop: '10px', fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                    Prompt Invocations - Last 7 Days
                  </div>
                </CardContent>
              </Card>
            </ContentGrid>

            <Card style={{ marginTop: '1.5rem' }}>
              <CardTitle>Optimization Opportunities</CardTitle>
              <Table>
                <thead>
                  <tr>
                    <TableHeader>Prompt</TableHeader>
                    <TableHeader>Issue</TableHeader>
                    <TableHeader>Impact</TableHeader>
                    <TableHeader>Suggested Improvement</TableHeader>
                    <TableHeader>Potential Gain</TableHeader>
                    <TableHeader>Actions</TableHeader>
                  </tr>
                </thead>
                <tbody>
                  <TableRow>
                    <TableCell>Customer Service Response</TableCell>
                    <TableCell>High Token Usage</TableCell>
                    <TableCell>Cost Efficiency</TableCell>
                    <TableCell>Streamline instructions, remove redundancies</TableCell>
                    <TableCell>~15% token reduction</TableCell>
                    <TableCell><Button style={{ padding: '0.3rem 0.6rem', fontSize: '0.8rem' }}>Optimize</Button></TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Policy Explanation</TableCell>
                    <TableCell>Low Clarity Score</TableCell>
                    <TableCell>User Experience</TableCell>
                    <TableCell>Add more specific instructions for simplification</TableCell>
                    <TableCell>~12% clarity improvement</TableCell>
                    <TableCell><Button style={{ padding: '0.3rem 0.6rem', fontSize: '0.8rem' }}>Optimize</Button></TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Fraud Detection</TableCell>
                    <TableCell>False Positive Rate</TableCell>
                    <TableCell>Accuracy</TableCell>
                    <TableCell>Add more context about legitimate patterns</TableCell>
                    <TableCell>~8% reduction in false positives</TableCell>
                    <TableCell><Button style={{ padding: '0.3rem 0.6rem', fontSize: '0.8rem' }}>Optimize</Button></TableCell>
                  </TableRow>
                </tbody>
              </Table>
            </Card>
          </TabContent>
        </TabContainer>

        <Card style={{ marginTop: '2rem' }}>
          <CardTitle>Prompt Engineering Best Practices</CardTitle>
          <CardContent>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '1rem', flexWrap: 'wrap', gap: '1rem' }}>
              <div style={{ flex: '1 1 30%', minWidth: '250px' }}>
                <div style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>Structure</div>
                <ul style={{ paddingLeft: '1.5rem', fontSize: '0.9rem', color: 'var(--text-secondary)' }}>
                  <li>Use clear sections with headers</li>
                  <li>Include specific instructions at the beginning</li>
                  <li>Use numbered lists for multi-step tasks</li>
                  <li>Include placeholders for dynamic content</li>
                  <li>End with expected output format</li>
                </ul>
              </div>

              <div style={{ flex: '1 1 30%', minWidth: '250px' }}>
                <div style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>Clarity</div>
                <ul style={{ paddingLeft: '1.5rem', fontSize: '0.9rem', color: 'var(--text-secondary)' }}>
                  <li>Be specific about the desired outcome</li>
                  <li>Define technical terms when necessary</li>
                  <li>Use consistent terminology throughout</li>
                  <li>Avoid ambiguous instructions</li>
                  <li>Provide examples for complex tasks</li>
                </ul>
              </div>

              <div style={{ flex: '1 1 30%', minWidth: '250px' }}>
                <div style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>Optimization</div>
                <ul style={{ paddingLeft: '1.5rem', fontSize: '0.9rem', color: 'var(--text-secondary)' }}>
                  <li>Test prompts with various inputs</li>
                  <li>Track performance metrics</li>
                  <li>Version control all changes</li>
                  <li>Optimize for token efficiency</li>
                  <li>Regularly review and update</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </PageContainer>
  );
};

export default PromptLibrary;
